/**
 * Paginazione degli elenchi lunghi, con la pagina nella query string: chi
 * apre una riga e torna indietro ritrova la stessa pagina. Piu elenchi nella
 * stessa vista usano chiavi diverse. Con una pagina sola la barra non c'e.
 * Il riferimento `inizio` va sull'elemento che contiene l'elenco: cambiando
 * pagina dal fondo, la vista torna al suo inizio.
 *
 * `usePaginazione` taglia un elenco gia tutto in memoria; `usePagine` da solo
 * la pagina e la barra, per gli elenchi che il server consegna a pagine.
 */

import { type MouseEvent, useRef } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import {
  PER_PAGINA,
  numeroPagine,
  paginaValida,
  righeDellaPagina,
  vociPaginazione,
} from './pagine.js';

export function usePaginazione<T>(elenco: readonly T[], chiave = 'pagina', perPagina = PER_PAGINA) {
  const { pagina, barra, inizio } = usePagine(numeroPagine(elenco.length, perPagina), chiave);
  return { righe: righeDellaPagina(elenco, pagina, perPagina), barra, inizio };
}

/**
 * La pagina dall'URL e la barra, dato il numero di pagine. Finche il server
 * non ha detto quante sono, `pagine` e null e la pagina e quella dell'URL.
 */
export function usePagine(pagine: number | null, chiave = 'pagina') {
  const [params, setParams] = useSearchParams();
  const { pathname } = useLocation();
  const pagina = paginaValida(params.get(chiave), pagine ?? Number.MAX_SAFE_INTEGER);
  const inizio = useRef<HTMLDivElement>(null);

  const conPagina = (p: number) => {
    const q = new URLSearchParams(params);
    if (p === 1) q.delete(chiave);
    else q.set(chiave, String(p));
    return q;
  };
  const collegamento = (p: number) => ({
    href: `${pathname}?${conPagina(p).toString()}`,
    // Cambiare pagina non allunga la cronologia, come i filtri.
    onClick: (e: MouseEvent) => {
      e.preventDefault();
      setParams(conPagina(p), { replace: true });
      const elemento = inizio.current;
      if (elemento && elemento.getBoundingClientRect().top < 0) elemento.scrollIntoView();
    },
  });

  const barra =
    pagine !== null && pagine > 1 ? (
      <Pagination>
        <PaginationContent>
          <PaginationItem>
            <PaginationPrevious
              text="Precedente"
              aria-label="Pagina precedente"
              aria-disabled={pagina === 1}
              tabIndex={pagina === 1 ? -1 : undefined}
              className={pagina === 1 ? 'pointer-events-none opacity-50' : undefined}
              {...collegamento(Math.max(1, pagina - 1))}
            />
          </PaginationItem>
          {vociPaginazione(pagina, pagine).map((voce, i) =>
            voce === 'puntini' ? (
              <PaginationItem key={`puntini-${i}`}>
                <PaginationEllipsis />
              </PaginationItem>
            ) : (
              <PaginationItem key={voce}>
                <PaginationLink
                  isActive={voce === pagina}
                  aria-label={`Pagina ${voce}`}
                  className="tabular-nums"
                  {...collegamento(voce)}
                >
                  {voce}
                </PaginationLink>
              </PaginationItem>
            ),
          )}
          <PaginationItem>
            <PaginationNext
              text="Successiva"
              aria-label="Pagina successiva"
              aria-disabled={pagina === pagine}
              tabIndex={pagina === pagine ? -1 : undefined}
              className={pagina === pagine ? 'pointer-events-none opacity-50' : undefined}
              {...collegamento(Math.min(pagine, pagina + 1))}
            />
          </PaginationItem>
        </PaginationContent>
      </Pagination>
    ) : null;

  return { pagina, barra, inizio };
}
